/**
 * The custom color picker's body — field with crew markers and crosshair,
 * readout, commit — shared by the desktop modal and the phone sheet, one
 * geometry prop, the `PlayerCountEditor` pattern.
 * `design/GT Crew.dc.html` / `design/GT Mobile Crew.dc.html` `ccShow`
 * overlays; DESIGN-CHANGELOG 2026-08-18.
 *
 * Two deliberate per-tree differences from the mirrors, kept here:
 *  - Marker hover is the only thing that NAMES a marker (initials are
 *    ambiguous — M is MARCUS and MIKE), so markers carry `data-tip` for the
 *    desktop tooltip layer; the drag works through them because pointer
 *    events bubble to the field.
 *  - The 430px column has no hover and no tooltip host at all, so the phone
 *    body carries the written `ON THE FIELD` legend (swatch + name per
 *    member) and the dashed footnote instead of tips nobody can reach.
 */

import { ColorField } from './ColorField.tsx';
import { useQd } from '../store/index.ts';
import { characterVM } from '../store/selectors.x2.ts';

export function CustomColorBody({ desktop }: { desktop?: boolean }): JSX.Element {
  const s = useQd();
  const v = characterVM(s);
  const mk = desktop ? { px: 20, fs: '7px' } : { px: 22, fs: '8px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: desktop ? '16px 18px 18px' : '14px' }}>
      <ColorField onPoint={s.setCcPoint}>
        {v.ccMarkers.map((m) => (
          <div
            key={m.key}
            data-tip={m.tip}
            style={{
              position: 'absolute',
              left: m.left,
              top: m.top,
              width: mk.px,
              height: mk.px,
              margin: -(mk.px / 2) + 'px 0 0 ' + -(mk.px / 2) + 'px',
              boxSizing: 'border-box',
              border: '1px solid ' + m.bd,
              background: m.bg,
              boxShadow: '0 0 0 1px rgba(0,0,0,.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: mk.fs, color: m.ink, transform: 'translateY(0.13em)' }}
            >
              {m.ini}
            </span>
          </div>
        ))}
        {v.ccHasSel ? (
          <div
            style={{
              position: 'absolute',
              left: v.ccX,
              top: v.ccY,
              width: '18px',
              height: '18px',
              margin: '-9px 0 0 -9px',
              border: '2px solid var(--gt-ink)',
              boxShadow: '0 0 0 1px #fff',
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </ColorField>
      {!desktop ? (
        <div style={{ display: 'flex', gap: '8px 12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span
            style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '8.5px', letterSpacing: '.08em', color: 'var(--gt-muted-2)' }}
          >
            ON THE FIELD
          </span>
          {v.ccMarkers.map((m) => (
            <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '14px', height: '14px', flex: 'none', border: '1px solid ' + m.bd, background: m.bg }} />
              <span
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: '8.5px',
                  letterSpacing: '.04em',
                  color: 'var(--gt-text-5)',
                  whiteSpace: 'nowrap',
                }}
              >
                {m.nm}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
        <span style={{ width: '34px', height: '34px', flex: 'none', border: '1px solid var(--gt-ink)', background: v.ccCol }} />
        <span
          style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '10px', letterSpacing: '.04em', color: 'var(--gt-text-3)', flex: 1, minWidth: 0 }}
        >
          {v.ccColTxt}
        </span>
      </div>
      {!desktop ? (
        <div
          style={{
            border: '1px dashed var(--gt-border-2)',
            padding: '10px 12px',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '8.5px',
            lineHeight: 1.7,
            color: 'var(--gt-muted-2)',
          }}
        >
          CHROMA RAMPS UP TO EACH HUE&apos;S PUREST POINT AND BACK DOWN, SO THE PURE COLORS ARE IN IT — RED
          MID-LEFT, GREEN HIGH, BLUE LOW. QUIETER HOUSE TONES ARE THE 20 TILES. GO DEEP ENOUGH AND YOUR
          INITIAL FLIPS TO LIGHT INK SO IT KEEPS READING.
        </div>
      ) : null}
      <span
        onClick={v.ccCommitHex ? () => void s.applyCustomColor(v.ccCommitHex) : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: desktop ? '42px' : '46px',
          border: '1px solid ' + v.ccBtnBd,
          color: v.ccBtnClr,
          fontFamily: 'var(--gt-font-mono)',
          fontSize: '10px',
          cursor: v.ccBtnCur,
          boxSizing: 'border-box',
        }}
      >
        USE THIS COLOR ▸
      </span>
    </div>
  );
}
